import React, { useEffect, useState } from 'react';
import { Alert, Button, Empty, Input, Select, Space, Table, Typography } from 'antd';
import type { TableProps } from 'antd';
import { errorMessage } from '../api';
import type { BillingApi, UserRow, UserSort, UserStatus } from '../billingApi';
import { StatusTag, STATUS_META } from '../components/StatusTag';
import { day } from '../lib/format';
import type { Route } from '../router';

const { Text } = Typography;

const PAGE_SIZE = 50;
const STATE_OPTIONS = (Object.keys(STATUS_META) as UserStatus[]).map((value) => ({
  value,
  label: STATUS_META[value].label,
}));

interface Query {
  search: string;
  state: UserStatus | null;
  sort: UserSort;
  direction: 'asc' | 'desc';
  page: number;
}

const SORT_KEYS: Record<string, UserSort> = {
  email: 'email',
  created_at: 'created_at',
  trial_ends_at: 'trial_ends_at',
  last_sign_in_at: 'last_sign_in_at',
};

/** Every account with its status; search by email, filter by status, open one for the details. */
export const UsersScreen: React.FC<{
  billing: BillingApi;
  navigate: (route: Route) => void;
}> = ({ billing, navigate }) => {
  const [query, setQuery] = useState<Query>({
    search: '',
    state: null,
    sort: 'created_at',
    direction: 'desc',
    page: 1,
  });
  const [result, setResult] = useState<{ users: UserRow[]; total: number } | null>(null);
  const [loaded, setLoaded] = useState<Query | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    billing
      .listUsers({
        search: query.search,
        state: query.state,
        sort: query.sort,
        direction: query.direction,
        page: query.page,
        pageSize: PAGE_SIZE,
      })
      .then((r) => {
        if (cancelled) return;
        setResult(r);
        setLoaded(query);
        setLoadError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoaded(query);
        setLoadError(errorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [billing, query, reloadKey]);

  const loading = loaded !== query && !loadError;
  const sortOrder = (key: UserSort) =>
    query.sort === key
      ? query.direction === 'asc'
        ? ('ascend' as const)
        : ('descend' as const)
      : null;

  const columns = [
    {
      title: 'Email',
      key: 'email',
      sorter: true,
      sortOrder: sortOrder('email'),
      render: (_: unknown, row: UserRow) => (
        <a onClick={() => navigate({ name: 'user', id: row.user_id })}>
          {row.email ?? '(no email)'}
        </a>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 150,
      render: (_: unknown, row: UserRow) => <StatusTag status={row.status} />,
    },
    {
      title: 'Signed up',
      key: 'created_at',
      width: 130,
      sorter: true,
      sortOrder: sortOrder('created_at'),
      render: (_: unknown, row: UserRow) => <Text type="secondary">{day(row.created_at)}</Text>,
    },
    {
      title: 'Trial ends',
      key: 'trial_ends_at',
      width: 130,
      sorter: true,
      sortOrder: sortOrder('trial_ends_at'),
      render: (_: unknown, row: UserRow) => <Text type="secondary">{day(row.trial_ends_at)}</Text>,
    },
    {
      title: 'Last sign-in',
      key: 'last_sign_in_at',
      width: 130,
      sorter: true,
      sortOrder: sortOrder('last_sign_in_at'),
      render: (_: unknown, row: UserRow) => (
        <Text type="secondary">{day(row.last_sign_in_at) || 'Never'}</Text>
      ),
    },
    {
      title: 'Last activity',
      key: 'last_activity_at',
      width: 130,
      render: (_: unknown, row: UserRow) => (
        <Text type="secondary">{day(row.last_activity_at) || 'None'}</Text>
      ),
    },
    {
      title: 'Plan',
      key: 'plan',
      width: 100,
      render: (_: unknown, row: UserRow) => row.plan ?? <Text type="secondary">None</Text>,
    },
  ];

  const onTableChange: TableProps<UserRow>['onChange'] = (pagination, _filters, sorter, extra) => {
    if (extra.action === 'paginate') {
      setQuery((q) => ({ ...q, page: pagination.current ?? 1 }));
    } else if (extra.action === 'sort') {
      const s = Array.isArray(sorter) ? sorter[0] : sorter;
      const key = typeof s?.columnKey === 'string' ? SORT_KEYS[s.columnKey] : undefined;
      setQuery((q) =>
        key && s.order
          ? { ...q, sort: key, direction: s.order === 'ascend' ? 'asc' : 'desc', page: 1 }
          : { ...q, sort: 'created_at', direction: 'desc', page: 1 }
      );
    }
  };

  return (
    <div>
      <Typography.Title level={3} style={{ margin: '0 0 16px' }}>
        Users
      </Typography.Title>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Search by email"
          aria-label="Search users"
          style={{ width: 300 }}
          onSearch={(value) => setQuery((q) => ({ ...q, search: value.trim(), page: 1 }))}
        />
        <Select
          allowClear
          placeholder="All statuses"
          aria-label="Status"
          style={{ width: 180 }}
          options={STATE_OPTIONS}
          value={query.state}
          onChange={(value) => setQuery((q) => ({ ...q, state: value ?? null, page: 1 }))}
        />
        {result && !loadError && (
          <Text type="secondary">
            {result.total.toLocaleString('en')} user{result.total === 1 ? '' : 's'}
          </Text>
        )}
      </Space>

      {loadError && (
        <Alert
          type="error"
          showIcon
          title="Could not load the users"
          description={loadError}
          action={<Button onClick={() => setReloadKey((k) => k + 1)}>Try again</Button>}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table<UserRow>
        rowKey="user_id"
        size="middle"
        loading={loading}
        dataSource={loadError ? [] : (result?.users ?? [])}
        columns={columns}
        scroll={{ x: 'max-content' }}
        showSorterTooltip={false}
        onChange={onTableChange}
        pagination={{
          current: query.page,
          pageSize: PAGE_SIZE,
          total: result?.total ?? 0,
          showSizeChanger: false,
          hideOnSinglePage: true,
        }}
        locale={{
          emptyText: (
            <Empty
              description={
                query.search || query.state ? 'No user matches this search.' : 'No users yet.'
              }
            />
          ),
        }}
      />
    </div>
  );
};
